import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { Scale, Lock, History, Users, Loader2 } from 'lucide-react';
import { API } from '../../App';
import { REGIME_STYLES, useLegalForm } from './useLegalForm';
import { LegalFormChangeDialog } from './LegalFormChangeDialog';
import { PartnersEditor, emptyPartner, partnersTotal } from './PartnersEditor';

const fmt = (d) => (d ? new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

export const LegalFormCard = ({ onChanged }) => {
  const { data, loading, refresh } = useLegalForm();
  const [changeOpen, setChangeOpen] = useState(false);
  const [partners, setPartners] = useState([]);
  const [savingPartners, setSavingPartners] = useState(false);

  useEffect(() => {
    if (data) setPartners(data.partners?.length ? data.partners : [emptyPartner(), emptyPartner()]);
  }, [data]);

  const savePartners = async () => {
    setSavingPartners(true);
    try {
      await axios.put(`${API}/business/legal-form/partners`, {
        partners: partners.map((p) => ({ ...p, profit_share_pct: parseFloat(p.profit_share_pct) || 0, email: p.email || null })),
      });
      toast.success('Partner register saved');
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save partners');
    } finally {
      setSavingPartners(false);
    }
  };

  const regimeLabel = data?.regimes?.[data?.regime]?.label;
  const partnersDirty = JSON.stringify(partners) !== JSON.stringify(data?.partners || []);

  return (
    <Card data-testid="legal-form-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Scale className="w-5 h-5 text-[#283F8B]" /> Legal form & tax regime
        </CardTitle>
        <CardDescription>Drives how profits are taxed, the return we prepare, your equity accounts and filing deadlines.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        ) : (
          <>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm text-muted-foreground">Legal form</p>
                <p className="font-medium text-foreground" data-testid="legal-form-current">{data?.legal_form_label || 'Not confirmed yet'}</p>
                {data?.confirmed && (
                  <div className="flex flex-wrap items-center gap-2 mt-1.5">
                    <Badge variant="outline" className={REGIME_STYLES[data.regime] || ''} data-testid="legal-form-current-regime">{regimeLabel?.split(' (')[0] || data.regime}</Badge>
                    <span className="text-xs text-muted-foreground" data-testid="legal-form-effective">Effective {fmt(data.effective_from)}</span>
                  </div>
                )}
              </div>
              {data?.is_owner ? (
                <Button variant="outline" size="sm" onClick={() => setChangeOpen(true)} disabled={!data?.confirmed} data-testid="legal-form-change-btn">
                  <Lock className="w-3.5 h-3.5 mr-1.5" /> Change
                </Button>
              ) : (
                <span className="text-xs text-muted-foreground flex items-center gap-1" data-testid="legal-form-owner-only"><Lock className="w-3 h-3" /> Owner only</span>
              )}
            </div>

            {data?.partners_required && (
              <>
                <Separator />
                <div className="space-y-2">
                  <p className="text-sm font-medium flex items-center gap-2"><Users className="w-4 h-4 text-violet-600" /> Partner register (profit share)</p>
                  <PartnersEditor partners={partners} onChange={setPartners} disabled={!data.is_owner} />
                  {data.is_owner && (
                    <Button size="sm" onClick={savePartners} disabled={savingPartners || !partnersDirty || Math.abs(partnersTotal(partners) - 100) > 0.01}
                            className="bg-[#283F8B] hover:bg-[#1f3270]" data-testid="partners-save">
                      {savingPartners && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Save partners
                    </Button>
                  )}
                </div>
              </>
            )}

            {data?.history?.length > 0 && (
              <>
                <Separator />
                <div className="space-y-1.5" data-testid="legal-form-history">
                  <p className="text-sm font-medium flex items-center gap-2"><History className="w-4 h-4 text-muted-foreground" /> History</p>
                  {[...data.history].reverse().map((h, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <span className="text-foreground">{data.options?.forms?.find((f) => f.code === h.legal_form)?.label || h.legal_form}
                        {h.from && <span className="text-muted-foreground"> · from {data.options?.forms?.find((f) => f.code === h.from)?.label || h.from}</span>}</span>
                      <span className="text-muted-foreground">{h.kind === 'changed' ? 'Changed' : 'Confirmed'} · effective {fmt(h.effective_from)}
                        {h.conversion_journal && <span className="ml-1 text-[#283F8B]" data-testid="legal-form-conversion-journal">· journal {h.conversion_journal.reference}</span>}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </CardContent>
      <LegalFormChangeDialog open={changeOpen} onOpenChange={setChangeOpen} current={data} onChanged={() => { refresh(); onChanged?.(); }} />
    </Card>
  );
};
